/**
 * The policy as a person edits it: each clause a section that says in a line what it does now,
 * and opens into plain questions with sensible answers. The sections read and write the same
 * document the workbench keeps as `.mojoup/policy.json`, so the form, the document and what a
 * machine receives never disagree; the raw document stays available at the end for anything a
 * newer workbench understands that this form does not yet ask about.
 */

import { AFTER_OUTSIDE_LABEL, OUTSIDE_MATCH, type AfterOutside, networkDecision, TOOL_ACTION_LABEL, TOOL_RISK_LABEL, TOOL_RISKS, toolDecision, toolsPolicySchema, workspacePolicySchema, type GovernedServer, type NetworkEndpoint, type NetworkRule, type ToolAction, type ToolRisk, type ToolRule, type ToolsPolicy, type WorkspacePolicy , microsoft365PolicySchema, CLOUD_PROVIDER_IDS, type CloudAgentsPolicy, type CloudProviderId, type ImageGenerationMode, type MediaPolicy, type A2aPolicy , TOOL_FLOOR } from '@aioe/shared';
import { Segment } from '@heroui-pro/react';
import { useState, type ReactNode } from 'react';

import { routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { PersonDecidesToggle, RISK_HINT, RISK_TEXT, usePersonDecides, VALUE_TEXT } from './remoteApprovals.tsx';
import { RemotePolicyForm } from './remotePolicy.tsx';
import { Button, ChoiceSelect, inputClass, StatusMark, Tick, Toggle } from './ui.tsx';

type Raw = Record<string, unknown>;

/** The document, and the policy it means once every default is filled in; nothing when it is not valid JSON. */
function read(json: string): { raw: Raw; policy: WorkspacePolicy } | { problem: string } {
  let raw: Raw;
  try {
    raw = JSON.parse(json) as Raw;
  } catch (e) {
    return { problem: `The document is not valid JSON: ${(e as Error).message}` };
  }
  const parsed = workspacePolicySchema.safeParse(raw);
  if (!parsed.success) return { problem: parsed.error.issues.slice(0, 3).map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') };
  return { raw, policy: parsed.data };
}

const list = (items: string[], none: string) => (items.length ? items.join(', ') : none);
const M365_CONTENT_TEXT: Record<'none' | 'redacted' | 'full', string> = { none: 'No text at all', redacted: 'Text with secrets and personal data masked', full: 'Text with only credentials masked' };
const DECISION_TEXT = { allow: 'Allow', redact: 'Redact', block: 'Block', ask: 'Ask the person' } as const;
const SENSITIVITIES = ['public', 'internal', 'confidential', 'restricted'] as const;
/** The providers in the words their own products use. */
export const CLOUD_PROVIDER_TEXT: Record<CloudProviderId, string> = { 'github-copilot': 'GitHub Copilot', cursor: 'Cursor', 'claude-code': 'Claude Code', codex: 'Codex', jules: 'Jules', devin: 'Devin', ci: 'Your own CI' };
const PROTOCOL_TEXT: Record<NetworkEndpoint['protocol'], string> = { tcp: 'A plain connection', rest: 'A web API (REST)', websocket: 'A WebSocket', graphql: 'GraphQL', mcp: 'MCP tools', 'json-rpc': 'JSON-RPC' };

/**
 * The clauses this form asks about itself. The manifest's sections (manifestClauses.tsx) leave these
 * out, so no clause is shown twice; `set` takes only these, so a new section cannot forget to be listed.
 */
export const FORM_CLAUSES = ['allowedRuntimes', 'allowedProviders', 'dlp', 'approvals', 'git', 'remote', 'microsoft365', 'agents', 'network', 'tools', 'cloudAgents', 'media', 'a2a'] as const satisfies ReadonlyArray<keyof WorkspacePolicy>;
type FormClause = (typeof FORM_CLAUSES)[number];

/**
 * `more`: further sections in the same list (the manifest's clauses), before the document.
 */
export function PolicyEditor({ json, onChange, more }: { json: string; onChange: (json: string) => void; more?: ReactNode }) {
  // Organisation-wide, outside the document: which risk levels each person decides (before any early return, as hooks are).
  const personDecides = usePersonDecides();
  const doc = read(json);
  if ('problem' in doc) {
    return (
      <div className="grid gap-2">
        <p className="text-sm text-danger">{doc.problem}</p>
        <Advanced json={json} onChange={onChange} open />
      </div>
    );
  }
  const { raw, policy: p } = doc;
  /** Write one clause back into the document, leaving everything else as it was written. Only the clauses this form owns (FORM_CLAUSES). */
  const set = <K extends FormClause>(clause: K, value: WorkspacePolicy[K] | undefined) => {
    const next: Raw = { ...raw };
    if (value === undefined) delete next[clause];
    else next[clause] = value;
    onChange(JSON.stringify(next, null, 2));
  };
  const net = p.network ?? workspacePolicySchema.shape.network.unwrap().parse({});
  const sandbox = p.agents?.sandbox ?? 'off';
  const tools = p.tools ?? toolsPolicySchema.parse({});
  const m365 = microsoft365PolicySchema.parse(p.microsoft365 ?? {});
  return (
    <div className="grid gap-4">
    <div className="overflow-hidden rounded-xl border border-separator">
      <Section title="Runtimes and providers" summary={`${list(p.allowedRuntimes ?? [], 'Any runtime')} · ${list(p.allowedProviders ?? [], 'any provider')}`}>
        <Row label="Agent runtimes allowed" hint="The coding agents a workbench may start. Leave empty to allow any.">
          <ListEditor items={p.allowedRuntimes ?? []} placeholder="claude-code" onChange={(v) => set('allowedRuntimes', v.length ? v : undefined)} />
        </Row>
        <Row label="Model providers allowed" hint="Leave empty to allow any.">
          <ListEditor items={p.allowedProviders ?? []} placeholder="anthropic" onChange={(v) => set('allowedProviders', v.length ? v : undefined)} />
        </Row>
      </Section>

      <Section title="Data protection" summary={`Secrets: ${DECISION_TEXT[p.dlp.onSecrets].toLowerCase()} · personal information: ${DECISION_TEXT[p.dlp.onPii].toLowerCase()} · ${list(p.dlp.blockSensitivity, 'nothing')} blocked`}>
        <Row label="Secrets in a prompt or an answer" hint="Keys, tokens and passwords an agent is about to send or show.">
          <Choice value={p.dlp.onSecrets} options={DECISION_TEXT} onChange={(v) => set('dlp', { ...p.dlp, onSecrets: v })} />
        </Row>
        <Row label="Personal information">
          <Choice value={p.dlp.onPii} options={DECISION_TEXT} onChange={(v) => set('dlp', { ...p.dlp, onPii: v })} />
        </Row>
        <Row label="Text that tries to redirect the agent" hint="Prompt injection found in a file, a page or a tool's answer.">
          <Choice value={p.dlp.onPromptInjection} options={{ allow: 'Allow', block: 'Block', ask: 'Ask the person' }} onChange={(v) => set('dlp', { ...p.dlp, onPromptInjection: v })} />
        </Row>
        <Row label="Never send content marked" hint="Content at these sensitivity levels does not leave the machine.">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {SENSITIVITIES.map((s) => (
              <Tick key={s} checked={p.dlp.blockSensitivity.includes(s)} onChange={(on) => set('dlp', { ...p.dlp, blockSensitivity: SENSITIVITIES.filter((x) => (x === s ? on : p.dlp.blockSensitivity.includes(x))) })}>
                {s[0]!.toUpperCase() + s.slice(1)}
              </Tick>
            ))}
          </div>
        </Row>
        <Row label="Check content with a classifier model" hint="A small model on the machine or a node reads prompts and answers before they go.">
          <Toggle checked={p.dlp.classify} onChange={(v) => set('dlp', { ...p.dlp, classify: v })} label="Check content with a classifier model" />
        </Row>
        {p.dlp.classify && (
          <>
            <Row label="When the classifier cannot answer in time" hint="It is down, the model is missing, or it took too long.">
              <Choice value={p.dlp.onUnclassified} options={{ allow: 'Carry on', ask: 'Ask the person', block: 'Block' }} onChange={(v) => set('dlp', { ...p.dlp, onUnclassified: v })} />
            </Row>
            <Row label="Classifier" hint="Where it runs and which model. A node id serves the whole project.">
              <div className="grid gap-2 sm:grid-cols-2">
                <input className={inputClass} value={p.dlp.classifier.provider} onChange={(e) => set('dlp', { ...p.dlp, classifier: { ...p.dlp.classifier, provider: e.target.value } })} placeholder="ollama" aria-label="Classifier provider" />
                <input className={inputClass} value={p.dlp.classifier.model ?? ''} onChange={(e) => set('dlp', { ...p.dlp, classifier: { ...p.dlp.classifier, model: e.target.value || undefined } })} placeholder="Model" aria-label="Classifier model" />
              </div>
            </Row>
            <Row label="Time allowed for a check" hint="In milliseconds, 50 to 5000.">
              <input type="number" min={50} max={5000} className={`${inputClass} w-28`} value={p.dlp.latencyBudgetMs} onChange={(e) => set('dlp', { ...p.dlp, latencyBudgetMs: Math.min(5000, Math.max(50, Number(e.target.value) || 300)) })} aria-label="Time allowed for a check" />
            </Row>
          </>
        )}
      </Section>

      <Section title="Approvals" summary={`From a remote device: ${list((['low', 'medium', 'high'] as const).filter((r) => p.approvals.remoteApprovals[r] === 'allow'), 'none')} risk${p.approvals.requireHumanReviewLabels.length ? ` · human review for ${p.approvals.requireHumanReviewLabels.join(', ')}` : ''}`}>
        {(['low', 'medium', 'high'] as const).map((risk) => (
          <Row key={risk} label={RISK_TEXT[risk]} hint={personDecides.decides.includes(risk) ? `${RISK_HINT[risk]} Each person chooses for their own workbenches; this answer applies until they do, and on nodes.` : RISK_HINT[risk]}>
            <div className="grid justify-items-end gap-1.5">
              <Pick value={p.approvals.remoteApprovals[risk]} options={VALUE_TEXT} label={`${risk} risk approvals`} onChange={(v) => set('approvals', { ...p.approvals, remoteApprovals: { ...p.approvals.remoteApprovals, [risk]: v } })} />
              <PersonDecidesToggle risk={risk} state={personDecides} />
            </div>
          </Row>
        ))}
        {personDecides.loaded ? <p className="pb-2 text-xs text-muted">Whether each person decides is saved straight away and applies under every policy, not only this one.</p> : null}
        {personDecides.error ? <p className="pb-2 text-xs text-danger">{personDecides.error}</p> : null}
        <Row label="Decisions agents record" hint="Which of them a person approves before they stand. Every decision is audited either way.">
          <Choice value={p.approvals.decisionApproval ?? 'unset'} options={{ unset: 'Each workbench decides', none: 'None: they stand as recorded', wider: "The project's and the organisation's", all: 'Every one' }} onChange={(v) => set('approvals', { ...p.approvals, decisionApproval: v === 'unset' ? undefined : v })} />
        </Row>
        <Row label="Tasks that need a person's review" hint="Tasks carrying one of these labels are not finished until a person has reviewed them.">
          <ListEditor items={p.approvals.requireHumanReviewLabels} placeholder="security" onChange={(v) => set('approvals', { ...p.approvals, requireHumanReviewLabels: v })} />
        </Row>
      </Section>

      <Section title="Git" summary={`${list(p.git.protectedBranches, 'No branch')} protected${p.git.requirePullRequest ? ' · pull request required' : ''}`}>
        <Row label="Protected branches" hint="Agents never push to these directly. A pattern such as release/* covers a family.">
          <ListEditor items={p.git.protectedBranches} placeholder="main" onChange={(v) => set('git', { ...p.git, protectedBranches: v })} />
        </Row>
        <Row label="Changes land through a pull request">
          <Toggle checked={p.git.requirePullRequest} onChange={(v) => set('git', { ...p.git, requirePullRequest: v })} label="Changes land through a pull request" />
        </Row>
      </Section>

      <Section title="Remote control" summary={`${p.remote.killSwitchLockedOn ? 'Kept switched off' : `${p.remote.allowTransports.length} ${p.remote.allowTransports.length === 1 ? 'way' : 'ways'} in`} · ${p.remote.maxScopes ? `${p.remote.maxScopes.length} things a paired device may do` : 'no limit on a paired device'}${p.remote.requireTls ? ' · encrypted only' : ''}`}>
        <RemotePolicyForm json={json} onChange={onChange} />
      </Section>

      <Section title="Microsoft 365: what run records carry" summary={`Run telemetry carries ${M365_CONTENT_TEXT[m365.telemetryContent].toLowerCase()}${m365.directExport?.enabled ? ' · machines send their own runs to Agent 365' : ''}`}>
        <Row label="What run telemetry carries" hint="What Agent 365 observability sees of a run: the prompt, the final message and tool arguments. Credentials are always masked; a run that met a data-protection block carries nothing.">
          <Choice value={m365.telemetryContent} options={M365_CONTENT_TEXT} onChange={(telemetryContent) => set('microsoft365', { ...m365, telemetryContent })} />
        </Row>
        <Row label="Machines send their own runs" hint="The direct path: each workbench and node sends its runs to Agent 365 itself with a short-lived token from the platform, instead of the platform forwarding them. Lower latency, more to go wrong on the machine.">
          <Toggle checked={m365.directExport?.enabled ?? false} label="Direct export" onChange={(enabled) => set('microsoft365', { ...m365, directExport: { enabled, maxChatSpansPerRun: m365.directExport?.maxChatSpansPerRun ?? 200 } })} />
        </Row>
      </Section>
      <Section title="Agent containment" summary={sandbox === 'required' ? 'Agents run in a sandbox, or not at all' : sandbox === 'preferred' ? 'Agents run in a sandbox where the machine has one' : 'Agents run directly on the machine'}>
        <Row label="Run agents in a sandbox" hint={sandbox === 'required' ? 'Work goes only to Linux nodes approved to sandbox agents; a workbench refuses it and says why. A subscription sign-in is placed inside the sandbox for the session.' : sandbox === 'preferred' ? 'Nodes that can sandbox agents are tried first; elsewhere agents run directly and the machine says so.' : 'Agents run with the access of the person who started them.'}>
          <Pick value={sandbox} options={{ off: 'Off', preferred: 'Where available', required: 'Always' }} label="Run agents in a sandbox" onChange={(v) => set('agents', { ...(p.agents ?? {}), sandbox: v })} />
        </Row>
      </Section>

      <Section title="Cloud agents" summary={cloudSummary(p.cloudAgents)}>
        <CloudAgentsForm clause={p.cloudAgents} onChange={(v) => set('cloudAgents', v)} />
      </Section>

      <Section title="Copilot agents this organisation talks to" summary={a2aSummary(p.a2a)}>
        <A2aForm clause={p.a2a} onChange={(v) => set('a2a', v)} />
      </Section>

      <Section title="Pictures" summary={mediaSummary(p.media)}>
        <MediaForm clause={p.media} onChange={(v) => set('media', v)} />
      </Section>

      <Section
        title="Where agents may connect"
        summary={`Other destinations ${net.defaultAction === 'deny' ? 'blocked' : 'allowed'} · ${net.allowHosts.length} always allowed · ${net.denyHosts.length} never · ${net.rules.length} ${net.rules.length === 1 ? 'rule' : 'rules'}${net.enforcement === 'audit' ? ' · only recording' : ''}`}
      >
        <NetworkForm net={net} onChange={(v) => set('network', v)} />
      </Section>

      <Section
        title="Tools agents may use"
        summary={`${p.tools ? (tools.enforcement === 'audit' ? 'Watching only' : 'Enforced') : 'Not set: watching only'} · reads ${TOOL_ACTION_LABEL[tools.defaults.reads].toLowerCase()} · changes ${TOOL_ACTION_LABEL[tools.defaults.changes].toLowerCase()} · destroys ${TOOL_ACTION_LABEL[tools.defaults.destroys].toLowerCase()} · ${tools.rules.length} ${tools.rules.length === 1 ? 'rule' : 'rules'}${tools.afterOutside === 'ask' ? ' · asks after outside content' : tools.afterOutside === 'off' ? ' · ignores outside content' : ''}`}
      >
        <ToolsForm tools={tools} onChange={(v) => set('tools', v)} />
      </Section>
      {more}
    </div>
      <Advanced json={json} onChange={onChange} />
    </div>
  );
}

/* ---------- cloud agents ---------- */

function cloudSummary(c: CloudAgentsPolicy | undefined): string {
  const allowed = c?.allowed ?? [];
  if (!allowed.length) return 'No cloud agents';
  return [allowed.map((x) => CLOUD_PROVIDER_TEXT[x]).join(', '), c?.requireHumanReview === false ? 'merge after the team review' : 'a person reviews first', c?.maxConcurrent ? `at most ${c.maxConcurrent} at once` : ''].filter(Boolean).join(' · ');
}

/** Which providers' cloud agents may take a team member's tasks; without any, the organisation allows none. */
function CloudAgentsForm({ clause, onChange }: { clause: CloudAgentsPolicy | undefined; onChange: (c: CloudAgentsPolicy | undefined) => void }) {
  const c = clause ?? {};
  const allowed = c.allowed ?? [];
  const write = (next: CloudAgentsPolicy) => onChange(next.allowed?.length || next.organisationKeys || next.requireHumanReview === false || next.maxConcurrent ? next : undefined);
  const toggle = (id: CloudProviderId, on: boolean) => write({ ...c, allowed: on ? [...allowed, id] : allowed.filter((x) => x !== id) });
  return (
    <>
      <Row label="Providers allowed" hint="A team member placed in the cloud sends its tasks to this provider's agent, under the person's own account. The work comes back as a branch the team reviews. Allow none and the organisation's workbenches place no member in the cloud.">
        <div className="flex max-w-md flex-wrap gap-x-4 gap-y-1.5 sm:justify-end">
          {CLOUD_PROVIDER_IDS.map((id) => (
            <Tick key={id} checked={allowed.includes(id)} onChange={(on) => toggle(id, on)}>
              {CLOUD_PROVIDER_TEXT[id]}
            </Tick>
          ))}
        </div>
      </Row>
      <Row label="A person reviews cloud work before it merges" hint="Off, cloud work may merge after the team's own review like any member's.">
        <Toggle checked={c.requireHumanReview !== false} label="A person reviews cloud work" onChange={(on) => write({ ...c, requireHumanReview: on ? undefined : false })} />
      </Row>
      <Row label="Keys the organisation pays for" hint="Off, only each person's own provider account is used. On, a key the organisation holds (a Cursor service account, a Devin organisation key) may be.">
        <Toggle checked={c.organisationKeys ?? false} label="Organisation keys" onChange={(on) => write({ ...c, organisationKeys: on || undefined })} />
      </Row>
      <Row label="Cloud tasks at once, per project" hint="Leave empty for no limit.">
        <input
          className={`${inputClass} w-24 tabular-nums`}
          type="number"
          min={1}
          max={100}
          aria-label="Cloud tasks at once"
          value={c.maxConcurrent ?? ''}
          onChange={(e) => {
            const n = Math.trunc(Number(e.target.value));
            write({ ...c, maxConcurrent: e.target.value && n >= 1 ? Math.min(100, n) : undefined });
          }}
        />
      </Row>
    </>
  );
}

/* ---------- Microsoft 365 Copilot calls our agents ---------- */

function a2aSummary(c: A2aPolicy | undefined): string {
  if (!c?.allowed) return 'Copilot cannot call our agents';
  return ['Copilot can call the agents made callable', c.callers?.length ? `${c.callers.length} Copilot ${c.callers.length === 1 ? 'agent' : 'agents'} only` : 'any Copilot agent', c.requireApproval === 'always' ? 'a person approves first' : c.requireApproval === 'changes' ? 'a person reviews changes' : ''].filter(Boolean).join(' · ');
}

/** Whether Microsoft 365 Copilot and Copilot Studio may call the agents made callable on projects' pages. */
function A2aForm({ clause, onChange }: { clause: A2aPolicy | undefined; onChange: (c: A2aPolicy | undefined) => void }) {
  const c: A2aPolicy = clause ?? { allowed: false };
  return (
    <>
      <Row label="Copilot may call our agents" hint="Specialists and teams made callable on a project's page answer Copilot Studio agents, as the person asking and with their permissions. Off, nothing is callable.">
        <Toggle checked={c.allowed} label="Copilot may call our agents" onChange={(allowed) => onChange(allowed || c.callers?.length || c.requireApproval ? { ...c, allowed } : undefined)} />
      </Row>
      {c.allowed ? (
        <>
          <Row label="Only these Copilot agents" hint="Their Entra application ids. Leave empty for any Copilot agent the person uses.">
            <ListEditor items={c.callers ?? []} placeholder="00000000-0000-0000-0000-000000000000" onChange={(v) => onChange({ ...c, callers: v.length ? v : undefined })} />
          </Row>
          <Row label="A person approves" hint="Never: a task starts like any other. Before it starts: a team's plan or a specialist waits for the person's yes. Before changes merge: what it changed waits for the person's review.">
            <Pick value={c.requireApproval ?? 'never'} options={{ never: 'Never', always: 'Before it starts', changes: 'Before changes merge' }} label="A person approves" onChange={(v) => onChange({ ...c, requireApproval: v === 'never' ? undefined : v })} />
          </Row>
        </>
      ) : null}
    </>
  );
}

/* ---------- pictures (AI image models) ---------- */

const IMAGE_MODE_TEXT: Record<ImageGenerationMode, string> = { off: 'Off', 'local-only': 'On this computer only', allowed: 'Allowed' };

function mediaSummary(m: MediaPolicy | undefined): string {
  const mode = m?.imageGeneration ?? 'off';
  if (mode === 'off') return 'No AI image models';
  return [IMAGE_MODE_TEXT[mode], m?.providers?.length ? m.providers.join(', ') : '', m?.maxImagesPerDay !== undefined ? `at most ${m.maxImagesPerDay} a day` : '', m?.approval === 'ask' ? 'each image waits for a yes' : ''].filter(Boolean).join(' · ');
}

/** Whether AI image models may make pictures (illustration and styling, always marked AI-generated); without the clause, an organisation's policy allows none. */
function MediaForm({ clause, onChange }: { clause: MediaPolicy | undefined; onChange: (c: MediaPolicy | undefined) => void }) {
  const m = clause ?? {};
  const mode = m.imageGeneration ?? 'off';
  const write = (next: MediaPolicy) => onChange(next.imageGeneration && next.imageGeneration !== 'off' ? next : next.providers?.length || next.maxImagesPerDay !== undefined || next.approval ? { ...next, imageGeneration: 'off' } : undefined);
  return (
    <>
      <Row label="AI image models" hint="Pictures for diagrams and documents: illustration and styling, never the boxes, arrows or labels, and every one marked as AI-generated. On this computer only allows a model that runs on the machine itself.">
        <Pick value={mode} options={IMAGE_MODE_TEXT} label="AI image models" onChange={(imageGeneration) => write({ ...m, imageGeneration })} />
      </Row>
      {mode !== 'off' ? (
        <>
          <Row label="Image providers allowed" hint="Leave empty to allow any provider the policy's model providers let through.">
            <ListEditor items={m.providers ?? []} placeholder="openai" onChange={(v) => write({ ...m, providers: v.length ? v : undefined })} />
          </Row>
          <Row label="Each picture an agent asks for waits for a person" hint="A picture a person asks for is their own yes either way.">
            <Toggle checked={m.approval === 'ask'} label="Wait for a person" onChange={(on) => write({ ...m, approval: on ? 'ask' : undefined })} />
          </Row>
          <Row label="Pictures a day, per machine" hint="Leave empty for no limit.">
            <input
              className={`${inputClass} w-24 tabular-nums`}
              type="number"
              min={0}
              max={10000}
              aria-label="Pictures a day"
              value={m.maxImagesPerDay ?? ''}
              onChange={(e) => {
                const n = Math.trunc(Number(e.target.value));
                write({ ...m, maxImagesPerDay: e.target.value !== '' && n >= 0 ? Math.min(10000, n) : undefined });
              }}
            />
          </Row>
        </>
      ) : null}
    </>
  );
}

/* ---------- tools agents may use ---------- */

const ACTIONS = TOOL_ACTION_LABEL;
/** What each choice does once a session has read a web page, a mail, an issue or anything else written outside the organisation. */
const AFTER_OUTSIDE_SENTENCE: Record<AfterOutside, string> = {
  off: 'Nothing changes once a session has read a web page, a mail or an issue.',
  record: 'Each call a session makes after reading a web page, a mail or an issue is marked so in the audit trail; nothing is held.',
  ask: 'Once a session has read a web page, a mail or an issue, a person is asked before any tool changes, deletes or reads private data. Off stays off.',
};

function ToolsForm({ tools, onChange }: { tools: ToolsPolicy; onChange: (t: ToolsPolicy) => void }) {
  const rule = (i: number, patch: Partial<ToolRule>) => onChange({ ...tools, rules: tools.rules.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const HINT: Record<ToolRisk, string> = { reads: 'Searching, listing, fetching.', changes: 'Creating, updating, sending.', destroys: 'Deleting, or anything that cannot be undone.' };
  return (
    <div className="grid gap-1">
      <p className="text-xs text-muted">Each tool server and its tools are set on the Tools page. Here: what a tool gets when nothing is set for it, and rules for this policy's projects. Where two things disagree, the stricter one wins.</p>
      <Row label="When a call breaks these rules" hint={tools.enforcement === 'audit' ? 'Nothing is stopped yet: each call that would be refused or held is recorded, so you can see what would change before you switch it on.' : 'A call that is Off is refused, and one that is Ask first waits for a person.'}>
        <Pick value={tools.enforcement} options={{ audit: 'Only record it', enforce: 'Enforce' }} label="When a call breaks these rules" onChange={(v) => onChange({ ...tools, enforcement: v })} />
      </Row>
      <Row label="After outside content" hint={AFTER_OUTSIDE_SENTENCE[tools.afterOutside]}>
        <Pick value={tools.afterOutside} options={AFTER_OUTSIDE_LABEL} label="After outside content" onChange={(v) => onChange({ ...tools, afterOutside: v })} />
      </Row>
      {TOOL_RISKS.map((risk) => (
        <Row key={risk} label={`Tools that ${risk === 'reads' ? 'only read' : risk === 'changes' ? 'change things' : 'destroy things'}`} hint={HINT[risk]}>
          <Pick value={tools.defaults[risk]} options={ACTIONS} label={`${TOOL_RISK_LABEL[risk]} tools`} onChange={(v) => onChange({ ...tools, defaults: { ...tools.defaults, [risk]: v } })} />
        </Row>
      ))}
      <Row label="A server that is not in the catalogue" hint="One a repository brings in its own configuration and nobody has published.">
        <Pick value={tools.unlistedServers} options={{ allow: 'Allowed', ask: 'Ask first', block: 'Blocked' }} label="A server that is not in the catalogue" onChange={(v) => onChange({ ...tools, unlistedServers: v })} />
      </Row>
      <div className="mt-2 grid gap-1">
        <div className="text-sm font-medium">Always refused</div>
        <p className="text-xs text-muted">Commands no mode, grant or approval may let through, a runtime's own bypass mode included: the agent is told the rule, nothing runs, and the refusal is recorded. These ship with the platform; add your own patterns below.</p>
        <ul className="grid gap-0.5 text-xs text-muted">
          {TOOL_FLOOR.map((r) => (
            <li key={r.id} className="flex gap-2">
              <span className="text-foreground">{r.words[0]!.toUpperCase() + r.words.slice(1)}</span>
            </li>
          ))}
        </ul>
        <Row label="Your organisation also refuses" hint="Patterns over the whole command line: * is any run of characters, ? one character. Example: *terraform destroy*">
          <ListEditor items={tools.denyCommands} placeholder="*terraform destroy*" onChange={(denyCommands) => onChange({ ...tools, denyCommands })} />
        </Row>
      </div>
      <Row label="What a recorded call keeps" hint="The arguments with credentials masked, or only a digest that proves what they were.">
        <Pick value={tools.recordArguments} options={{ redacted: 'Masked arguments', none: 'A digest only' }} label="What a recorded call keeps" onChange={(v) => onChange({ ...tools, recordArguments: v })} />
      </Row>

      <div className="mt-2 flex items-baseline justify-between gap-3">
        <div>
          <div className="text-sm font-medium">Rules</div>
          <div className="text-xs text-muted">Say more for these projects: turn tools off, or let one team role use a tool without asking.</div>
        </div>
        <Button size="sm" variant="secondary" onPress={() => onChange({ ...tools, rules: [...tools.rules, { server: '*', action: 'ask' }] })}>
          Add a rule
        </Button>
      </div>
      {tools.rules.map((r, i) => (
        <div key={i} className="mt-2 grid gap-1 rounded-lg border border-separator p-3">
          <div className="flex items-center gap-2">
            <input className={`${inputClass} font-medium`} value={r.server} onChange={(e) => rule(i, { server: e.target.value || '*' })} placeholder="A server's name, or * for every server" aria-label="Server" />
            <Button size="sm" variant="ghost" onPress={() => onChange({ ...tools, rules: tools.rules.filter((_, j) => j !== i) })}>
              Remove
            </Button>
          </div>
          <Row label="Tools" hint="By name; delete_* covers a family. Every tool when empty.">
            <ListEditor items={r.tools ?? []} placeholder="delete_*" onChange={(v) => rule(i, { tools: v.length ? v : undefined })} />
          </Row>
          <Row label="Only tools of one class">
            <Choice value={r.risk ?? 'any'} options={{ any: 'Any class', ...TOOL_RISK_LABEL }} onChange={(v) => rule(i, { risk: v === 'any' ? undefined : v })} />
          </Row>
          <Row label="Only for agents in these roles" hint="Team roles, such as planner or reviewer. Every agent when empty.">
            <ListEditor items={r.roles ?? []} placeholder="planner" onChange={(v) => rule(i, { roles: v.length ? v : undefined })} />
          </Row>
          {(r.match === undefined || r.match === OUTSIDE_MATCH) && (
            <Row label="Only after outside content" hint="The rule holds once the session has read a web page, a mail or an issue.">
              <Tick checked={r.match === OUTSIDE_MATCH} label="Only after outside content" onChange={(on) => rule(i, { match: on ? OUTSIDE_MATCH : undefined })} />
            </Row>
          )}
          <Row label="These tools are">
            <Pick value={r.action} options={ACTIONS} label="These tools are" onChange={(v) => rule(i, { action: v })} />
          </Row>
        </div>
      ))}
      <TryTool tools={tools} />
    </div>
  );
}

const PINNED = '0'.repeat(64);

/** Ask the policy about one call, with the organisation's servers and reviews as they stand now. */
function TryTool({ tools }: { tools: ToolsPolicy }) {
  const seen = useLoad(() => routes.tools(), []);
  const [fingerprint, setFingerprint] = useState('');
  const [tool, setTool] = useState('');
  const [role, setRole] = useState('');
  const all = seen.data?.servers ?? [];
  const server = all.find((s) => s.fingerprint === fingerprint);
  const view = server?.tools.find((t) => t.name === tool.trim());
  // The clause as a machine would receive it: the published servers with each tool's class, setting and review.
  const servers: GovernedServer[] = all
    .filter((s) => s.status !== 'unlisted')
    .map((s) => ({ id: s.catalogueId ?? s.fingerprint, name: s.name, fingerprint: s.fingerprint, status: s.status === 'withdrawn' ? 'withdrawn' : 'published', tools: Object.fromEntries(s.tools.map((t) => [t.name, { risk: t.risk, ...(t.action ? { action: t.action } : {}), ...(t.readsPrivate ? { readsPrivate: true } : {}), ...(t.status !== 'new' ? { hash: PINNED } : {}) }])) }));
  const verdict = fingerprint && tool.trim() ? toolDecision({ ...tools, servers }, { fingerprint: server?.fingerprint ?? 'f'.repeat(64), serverName: server?.name ?? 'a server', tool: tool.trim(), ...(view?.status === 'reviewed' ? { hash: PINNED } : view ? { hash: '1'.repeat(64) } : {}), ...(view ? { risk: view.reportedRisk } : {}), ...(role.trim() ? { role: role.trim() } : {}) }) : undefined;
  const word = (a: ToolAction | undefined) => (a === 'ask' ? 'A person is asked first' : a === 'off' ? 'Refused' : 'Allowed');
  return (
    <div className="mt-3 grid gap-2 rounded-lg bg-surface-secondary p-3">
      <div>
        <div className="text-sm font-medium">Try a Tool</div>
        <div className="text-xs text-muted">See what this policy would do with a call before you publish it.</div>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <ChoiceSelect
          label="Server to try"
          className="w-full"
          value={fingerprint || undefined}
          placeholder="Choose a server"
          options={{
            ...Object.fromEntries(all.map((s) => [s.fingerprint, `${s.name}${s.status === 'unlisted' ? ' (not published)' : s.status === 'withdrawn' ? ' (withdrawn)' : ''}`])),
            other: 'A server nobody has reported',
          }}
          onChange={(v) => (setFingerprint(v), setTool(''))}
        />
        {server?.tools.length ? (
          <ChoiceSelect label="Tool to try" className="w-full" value={tool || undefined} placeholder="Choose a tool" options={Object.fromEntries(server.tools.map((t) => [t.name, t.name]))} onChange={setTool} />
        ) : (
          <input className={inputClass} value={tool} onChange={(e) => setTool(e.target.value)} placeholder="A tool's name" aria-label="Tool to try" />
        )}
        <input className={inputClass} value={role} onChange={(e) => setRole(e.target.value)} placeholder="Team role, such as planner" aria-label="Role" />
      </div>
      {verdict && (
        <div className="text-sm">
          <StatusMark state={verdict.would ? 'medium' : verdict.action === 'allow' ? 'connected' : verdict.action === 'ask' ? 'medium' : 'error'} label={verdict.would ? 'Recorded, not stopped' : word(verdict.action)} />
          <span className="text-muted">
            {' '}
            · {verdict.reason}
            {verdict.would ? `. Enforced: ${word(verdict.would).toLowerCase()}.` : ''}
          </span>
        </div>
      )}
    </div>
  );
}

/* ---------- where agents may connect ---------- */

type Net = NonNullable<WorkspacePolicy['network']>;

function NetworkForm({ net, onChange }: { net: Net; onChange: (n: Net) => void }) {
  const rule = (i: number, patch: Partial<NetworkRule>) => onChange({ ...net, rules: net.rules.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  return (
    <div className="grid gap-1">
      <p className="text-xs text-muted">Enforced on machines that run agents in a sandbox. The machine itself, link-local addresses and cloud metadata services are never reachable, whatever is set here.</p>
      <Row label="Other destinations" hint="Anything not named below.">
        <Pick value={net.defaultAction} options={{ allow: 'Allowed', deny: 'Blocked' }} label="Other destinations" onChange={(v) => onChange({ ...net, defaultAction: v })} />
      </Row>
      <Row label="When a connection breaks the policy" hint={net.enforcement === 'audit' ? 'Nothing is stopped yet: each breach is recorded, so you can see what the policy would block before you switch it on.' : 'It is stopped, and recorded.'}>
        <Pick value={net.enforcement} options={{ enforce: 'Block it', audit: 'Only record it' }} label="When a connection breaks the policy" onChange={(v) => onChange({ ...net, enforcement: v })} />
      </Row>
      <Row label="Always allowed" hint="A host name, or *.example.com for everything under it.">
        <ListEditor items={net.allowHosts} placeholder="registry.npmjs.org" onChange={(v) => onChange({ ...net, allowHosts: v })} />
      </Row>
      <Row label="Never allowed" hint="Wins over everything else.">
        <ListEditor items={net.denyHosts} placeholder="pastebin.com" onChange={(v) => onChange({ ...net, denyHosts: v })} />
      </Row>

      <div className="mt-2 flex items-baseline justify-between gap-3">
        <div>
          <div className="text-sm font-medium">Rules</div>
          <div className="text-xs text-muted">Say more about one destination: which programs may reach it, and which requests or tools.</div>
        </div>
        <Button size="sm" variant="secondary" onPress={() => onChange({ ...net, rules: [...net.rules, { name: `Rule ${net.rules.length + 1}`, endpoints: [{ host: 'example.com', protocol: 'tcp' }], programs: [] }] })}>
          Add a rule
        </Button>
      </div>
      {net.rules.map((r, i) => (
        <div key={i} className="mt-2 grid gap-2 rounded-lg border border-separator p-3">
          <div className="flex items-center gap-2">
            <input className={`${inputClass} font-medium`} value={r.name} onChange={(e) => rule(i, { name: e.target.value })} aria-label="Rule name" />
            <Button size="sm" variant="ghost" onPress={() => onChange({ ...net, rules: net.rules.filter((_, j) => j !== i) })}>
              Remove
            </Button>
          </div>
          {r.endpoints.map((e, k) => {
            const endpoint = (patch: Partial<NetworkEndpoint>) => rule(i, { endpoints: r.endpoints.map((x, m) => (m === k ? { ...x, ...patch } : x)) });
            return (
              <div key={k} className="grid gap-2 rounded-lg bg-surface-secondary p-2">
                <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_8rem_12rem]">
                  <input className={inputClass} value={e.host} onChange={(ev) => endpoint({ host: ev.target.value })} placeholder="api.github.com" aria-label="Destination" />
                  <input className={inputClass} value={(e.ports ?? []).join(', ')} onChange={(ev) => endpoint({ ports: portsOf(ev.target.value) })} placeholder="Any port" aria-label="Ports" />
                  <ChoiceSelect label="What is spoken there" className="w-full" value={e.protocol} options={PROTOCOL_TEXT as Record<NetworkEndpoint['protocol'], string>} onChange={(p) => endpoint({ protocol: p })} />
                </div>
                {e.protocol === 'rest' && (
                  <>
                    <Row label="Requests allowed" hint="GET /repos/** · any request when empty.">
                      <ListEditor items={(e.allow ?? []).map(requestText)} placeholder="GET /repos/**" onChange={(v) => endpoint({ allow: v.length ? v.map(requestOf) : undefined })} />
                    </Row>
                    <Row label="Requests refused">
                      <ListEditor items={(e.deny ?? []).map(requestText)} placeholder="DELETE /**" onChange={(v) => endpoint({ deny: v.length ? v.map(requestOf) : undefined })} />
                    </Row>
                  </>
                )}
                {e.protocol === 'mcp' && (
                  <>
                    <Row label="Tools allowed" hint="Any tool when empty.">
                      <ListEditor items={e.tools?.allow ?? []} placeholder="search_issues" onChange={(v) => endpoint({ tools: { ...(e.tools ?? {}), allow: v.length ? v : undefined } })} />
                    </Row>
                    <Row label="Tools refused">
                      <ListEditor items={e.tools?.deny ?? []} placeholder="delete_issue" onChange={(v) => endpoint({ tools: { ...(e.tools ?? {}), deny: v.length ? v : undefined } })} />
                    </Row>
                  </>
                )}
                {r.endpoints.length > 1 && (
                  <div>
                    <Button size="sm" variant="ghost" onPress={() => rule(i, { endpoints: r.endpoints.filter((_, m) => m !== k) })}>
                      Remove this destination
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
          <div>
            <Button size="sm" variant="ghost" onPress={() => rule(i, { endpoints: [...r.endpoints, { host: 'example.com', protocol: 'tcp' }] })}>
              Add a destination
            </Button>
          </div>
          <Row label="Programs that may use it" hint="By name or path. Any program when empty.">
            <ListEditor items={r.programs} placeholder="git" onChange={(v) => rule(i, { programs: v })} />
          </Row>
          <Row label="When this rule is broken">
            <Choice value={r.enforcement ?? 'policy'} options={{ policy: 'As the policy says', enforce: 'Block it', audit: 'Only record it' }} onChange={(v) => rule(i, { enforcement: v === 'policy' ? undefined : v })} />
          </Row>
        </div>
      ))}
      <TryDestination net={net} />
    </div>
  );
}

const portsOf = (text: string): number[] | undefined => {
  const ports = text.split(/[\s,]+/).map(Number).filter((n) => Number.isInteger(n) && n >= 1 && n <= 65535);
  return ports.length ? ports : undefined;
};
const requestText = (r: { method?: string; path?: string }) => `${r.method ?? '*'} ${r.path ?? '/**'}`;
const requestOf = (text: string): { method?: string; path?: string } => {
  const [a, b] = text.trim().split(/\s+/, 2);
  if (b === undefined) return a?.startsWith('/') ? { path: a } : { method: a?.toUpperCase() };
  return { ...(a && a !== '*' ? { method: a.toUpperCase() } : {}), path: b };
};

/** Ask the policy about one connection, in the words a person would use. */
function TryDestination({ net }: { net: Net }) {
  const [host, setHost] = useState('');
  const [program, setProgram] = useState('');
  const [what, setWhat] = useState('');
  const h = host.trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  const [name, port] = h.includes(':') && !h.startsWith('[') ? [h.slice(0, h.lastIndexOf(':')), Number(h.slice(h.lastIndexOf(':') + 1)) || undefined] : [h, undefined];
  const w = what.trim();
  const request = w.includes(' ') || w.startsWith('/') ? requestOf(w) : undefined;
  const verdict = name ? networkDecision(net, { host: name, port, program: program.trim() || undefined, ...(request ? { method: request.method, path: request.path } : w ? { tool: w } : {}) }) : undefined;
  return (
    <div className="mt-3 grid gap-2 rounded-lg bg-surface-secondary p-3">
      <div>
        <div className="text-sm font-medium">Try a Destination</div>
        <div className="text-xs text-muted">See what this policy would do with a connection before you publish it.</div>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <input className={inputClass} value={host} onChange={(e) => setHost(e.target.value)} placeholder="api.github.com" aria-label="Destination to try" />
        <input className={inputClass} value={program} onChange={(e) => setProgram(e.target.value)} placeholder="Program, such as git" aria-label="Program" />
        <input className={inputClass} value={what} onChange={(e) => setWhat(e.target.value)} placeholder="GET /repos/x or a tool name" aria-label="Request or tool" />
      </div>
      {verdict && (
        <div className="text-sm">
          <StatusMark state={verdict.action === 'allow' ? 'connected' : verdict.action === 'audit' ? 'medium' : 'error'} label={verdict.action === 'allow' ? 'Allowed' : verdict.action === 'audit' ? 'Recorded, not blocked' : 'Blocked'} />
          <span className="text-muted">
            {' '}
            · {verdict.reason}
          </span>
        </div>
      )}
    </div>
  );
}

/* ---------- the pieces ---------- */

/**
 * One part of a policy: its title, and under it at full width what it does now. Opens into its
 * questions. The manifest's clauses use the same piece, so the list reads as one.
 */
export function PolicySection({ title, summary, aside, children }: { title: string; summary: ReactNode; aside?: ReactNode; children: ReactNode }) {
  return (
    <details className="group border-t border-separator first:border-t-0">
      <summary className="grid cursor-pointer list-none gap-0.5 px-4 py-3 hover:bg-surface-secondary/50 [&::-webkit-details-marker]:hidden">
        <span className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-medium">{title}</span>
          {aside ? <span className="shrink-0 text-xs text-warning">{aside}</span> : null}
        </span>
        <span className="text-xs text-muted">{summary}</span>
      </summary>
      <div className="px-4 pb-4">{children}</div>
    </details>
  );
}
const Section = PolicySection;

/** A small heading inside the section list: one of the manifest's groups. */
export function PolicyGroupHeading({ children }: { children: ReactNode }) {
  return <div className="border-t border-separator bg-surface-secondary/40 px-4 pb-2 pt-3 text-xs font-medium text-muted">{children}</div>;
}

function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1.5 py-2">
      <div className="min-w-48 flex-1 basis-56">
        <div className="text-sm">{label}</div>
        {hint && <div className="text-xs text-muted">{hint}</div>}
      </div>
      {/* The answer keeps its own width and drops under the question when the two do not fit side by side. */}
      <div className="max-w-full sm:text-right">{children}</div>
    </div>
  );
}

function Choice<T extends string>({ value, options, onChange }: { value: T; options: Record<T, string>; onChange: (v: T) => void }) {
  return <ChoiceSelect value={value} options={options} onChange={onChange} label="Choose" />;
}

/** Two or three answers side by side. */
function Pick<T extends string>({ value, options, label, onChange }: { value: T; options: Record<T, string>; label: string; onChange: (v: T) => void }) {
  return (
    <Segment aria-label={label} size="sm" selectedKey={value} onSelectionChange={(k) => onChange(String(k) as T)} className="w-fit">
      {(Object.keys(options) as T[]).map((k) => (
        <Segment.Item key={k} id={k}>
          {options[k]}
        </Segment.Item>
      ))}
    </Segment>
  );
}

/** A short list a person adds to and takes from: type, press Enter. */
function ListEditor({ items, placeholder, onChange }: { items: string[]; placeholder: string; onChange: (items: string[]) => void }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (v && !items.includes(v)) onChange([...items, v]);
    setDraft('');
  };
  return (
    <div className="grid gap-1.5 sm:justify-items-end">
      {items.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 sm:justify-end">
          {items.map((it) => (
            <li key={it} className="inline-flex items-center gap-1 rounded-full bg-surface-secondary py-0.5 pl-2.5 pr-1 text-xs">
              <span className="mono">{it}</span>
              <button type="button" className="rounded-full px-1.5 text-muted hover:text-foreground" onClick={() => onChange(items.filter((x) => x !== it))} aria-label={`Remove ${it}`}>
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        className={`${inputClass} sm:w-64`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
        placeholder={`${placeholder} and press Enter`}
        aria-label={`Add: ${placeholder}`}
      />
    </div>
  );
}

/** The document itself, last and apart: for what a newer workbench understands that this form does not ask about. */
function Advanced({ json, onChange, open }: { json: string; onChange: (json: string) => void; open?: boolean }) {
  return (
    <details className="rounded-xl border border-dashed border-separator" open={open}>
      <summary className="grid cursor-pointer list-none gap-0.5 px-4 py-3 [&::-webkit-details-marker]:hidden">
        <span className="text-sm font-medium">Edit as JSON</span>
        <span className="text-xs text-muted">The document a workbench keeps as .mojoup/policy.json. Changes here show in the sections above.</span>
      </summary>
      <div className="px-4 pb-4">
        <textarea className={`${inputClass} mono min-h-72`} value={json} onChange={(e) => onChange(e.target.value)} spellCheck={false} aria-label="Policy document" />
      </div>
    </details>
  );
}
